import {
	AlertCircle,
	ChevronDown,
	ClipboardList,
	MoreVertical,
	PenLine,
	Send,
	UserPlus,
	UserRoundCheck,
	UserRoundX,
	Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { TEAM_ROLES } from "@/features/onboarding/data";
import { useTeamTimeEntries } from "@/features/presence/hooks/use-team-time-entries";
import type { TimeEntry } from "@/features/presence/types";
import { latestEntryByWorker } from "@/features/presence/utils/day";
import { formatClock } from "@/features/presence/utils/format";
import { dayRange } from "@/features/presence/utils/week";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { currentWorkDay } from "@/lib/format";
import { useSetWorkerActive } from "../../hooks/use-set-worker-active";
import { useSetWorkerTimeClock } from "../../hooks/use-set-worker-time-clock";
import {
	activeWorkers,
	inactiveWorkers,
	useWorkers,
} from "../../hooks/use-workers";
import type { Worker } from "../../types";
import {
	initials,
	memberAccent,
	ROLE_ACCENT,
	type RoleAccent,
} from "../../utils/format";
import { InviteDialog } from "../invite-dialog";
import { WorkerDialog } from "../worker-dialog";

type PresenceState = "present" | "done" | "absent";

function presenceState(entry: TimeEntry | undefined): PresenceState {
	if (!entry) return "absent";
	return entry.clockedOutAt ? "done" : "present";
}

interface RoleGroup {
	key: string;
	label: string;
	description?: string;
	icon: typeof Users;
	accent: RoleAccent;
	workers: Worker[];
}

interface RowContext {
	entriesByWorker: Map<string, TimeEntry>;
	showPresence: boolean;
	showToggle: boolean;
	canManage: boolean;
	canInvite: boolean;
	onEdit: (worker: Worker) => void;
	onInvite: (worker: Worker) => void;
	onDeactivate: (worker: Worker) => void;
	onReactivate: (worker: Worker) => void;
}

function groupByRole(workers: Worker[]): RoleGroup[] {
	const groups: RoleGroup[] = TEAM_ROLES.map((role) => ({
		key: role.id,
		label: role.label,
		description: role.description,
		icon: role.icon,
		accent: ROLE_ACCENT[role.id],
		workers: workers.filter((worker) => worker.role === role.id),
	}));

	const manual = workers.filter((worker) => worker.role === "");
	if (manual.length > 0) {
		groups.push({
			key: "manual",
			label: "Cadastro manual",
			description: "Pessoas sem acesso ao app",
			icon: ClipboardList,
			accent: "muted",
			workers: manual,
		});
	}

	const known = new Set<string>(TEAM_ROLES.map((role) => role.id));
	const others = workers.filter(
		(worker) => worker.role !== "" && !known.has(worker.role),
	);
	if (others.length > 0) {
		groups.push({
			key: "outros",
			label: "Outros",
			icon: Users,
			accent: "muted",
			workers: others,
		});
	}

	return groups.filter((group) => group.workers.length > 0);
}

function WorkerTimeClockToggle({ worker }: { worker: Worker }) {
	const setTimeClock = useSetWorkerTimeClock();

	async function handleChange(checked: boolean) {
		try {
			await setTimeClock.mutateAsync({
				workerId: worker.id,
				timeClockEnabled: checked,
			});
			toast.success(checked ? "Bate ponto ativado" : "Bate ponto desativado");
		} catch {
			toast.error("Não foi possível atualizar o bate ponto.");
		}
	}

	return (
		<div className="member-row__toggle">
			<span className="member-row__toggle-label">Bate ponto</span>
			<Switch
				checked={worker.timeClockEnabled}
				disabled={setTimeClock.isPending}
				aria-label={`Bate ponto de ${worker.name}`}
				onCheckedChange={handleChange}
			/>
		</div>
	);
}

function WorkerActions({
	worker,
	context,
}: {
	worker: Worker;
	context: RowContext;
}) {
	const linked = worker.userId != null;
	const canInvite = context.canInvite && !linked && worker.active;
	if (!context.canManage && !canInvite) return null;

	return (
		<DropdownMenu modal={false}>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="member-row__actions"
					aria-label={`Ações de ${worker.name}`}
				>
					<MoreVertical />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				{context.canManage && worker.active && (
					<DropdownMenuItem onClick={() => context.onEdit(worker)}>
						<PenLine /> Editar
					</DropdownMenuItem>
				)}
				{canInvite && (
					<DropdownMenuItem onClick={() => context.onInvite(worker)}>
						<Send /> Convidar
					</DropdownMenuItem>
				)}
				{context.canManage && worker.active && (
					<DropdownMenuItem
						variant="destructive"
						onClick={() => context.onDeactivate(worker)}
					>
						<UserRoundX /> Desativar
					</DropdownMenuItem>
				)}
				{context.canManage && !worker.active && (
					<DropdownMenuItem onClick={() => context.onReactivate(worker)}>
						<UserRoundCheck /> Reativar
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function WorkerRow({
	worker,
	context,
}: {
	worker: Worker;
	context: RowContext;
}) {
	const linked = worker.userId != null;
	const entry = context.entriesByWorker.get(worker.id);
	const state = presenceState(entry);
	const showPresence = context.showPresence && worker.active;
	const detail = worker.jobTitle || worker.userEmail;

	return (
		<div
			className="member-row"
			data-linked={linked}
			data-active={worker.active}
		>
			<div className="member-row__avatar-wrapper">
				<Avatar size="lg">
					<AvatarFallback
						className="member-row__avatar"
						data-accent={memberAccent(worker.name)}
					>
						{initials(worker.name)}
					</AvatarFallback>
				</Avatar>
				{showPresence && (
					<span className="member-row__dot" data-presence={state} />
				)}
			</div>
			<div className="member-row__info">
				<div className="member-row__title">
					<p className="member-row__name">{worker.name}</p>
					<Badge
						variant={linked ? "success" : "outline"}
						className="member-row__badge"
						data-linked={linked}
					>
						{linked ? "Com app" : "Sem app"}
					</Badge>
				</div>
				{detail && <p className="member-row__detail">{detail}</p>}
			</div>
			{showPresence && (
				<span className="member-row__check-in" data-presence={state}>
					{entry ? `↳ ${formatClock(entry.clockedInAt)}` : "—"}
				</span>
			)}
			{context.showToggle && worker.active && (
				<WorkerTimeClockToggle worker={worker} />
			)}
			<WorkerActions worker={worker} context={context} />
		</div>
	);
}

function RoleGroupCard({
	group,
	context,
}: {
	group: RoleGroup;
	context: RowContext;
}) {
	const Icon = group.icon;
	return (
		<Card className="role-group">
			<div className="team-accent-strip" data-accent={group.accent} />
			<CardHeader className="role-group__header">
				<div className="role-group__heading">
					<span
						className="team-accent-tile"
						data-accent={group.accent}
						data-size="md"
					>
						<Icon />
					</span>
					<div className="role-group__titles">
						<h2 className="role-group__title">{group.label}</h2>
						{group.description && (
							<p className="role-group__description">{group.description}</p>
						)}
					</div>
				</div>
				<Badge variant="outline" className="role-group__count">
					{group.workers.length}
				</Badge>
			</CardHeader>
			<CardContent className="role-group__content">
				<div className="role-group__members">
					{group.workers.map((worker) => (
						<WorkerRow key={worker.id} worker={worker} context={context} />
					))}
				</div>
			</CardContent>
		</Card>
	);
}

function InactiveWorkersCard({
	workers,
	context,
}: {
	workers: Worker[];
	context: RowContext;
}) {
	const [open, setOpen] = useState(false);

	return (
		<Collapsible open={open} onOpenChange={setOpen} asChild>
			<Card className="role-group members-inactive">
				<div className="team-accent-strip" data-accent="muted" />
				<CardHeader className="role-group__header">
					<CollapsibleTrigger asChild>
						<button type="button" className="members-inactive__trigger">
							<span className="role-group__heading">
								<span
									className="team-accent-tile"
									data-accent="muted"
									data-size="md"
								>
									<UserRoundX />
								</span>
								<span className="role-group__titles">
									<span className="role-group__title">Desativados</span>
									<span className="role-group__description">
										Fora da folha de presença. Histórico de ponto preservado.
									</span>
								</span>
							</span>
							<span className="members-inactive__meta">
								<Badge variant="outline" className="role-group__count">
									{workers.length}
								</Badge>
								<ChevronDown className="members-inactive__chevron" />
							</span>
						</button>
					</CollapsibleTrigger>
				</CardHeader>
				<CollapsibleContent>
					<CardContent className="role-group__content">
						<div className="role-group__members">
							{workers.map((worker) => (
								<WorkerRow key={worker.id} worker={worker} context={context} />
							))}
						</div>
					</CardContent>
				</CollapsibleContent>
			</Card>
		</Collapsible>
	);
}

function MembersSkeletonCard() {
	return (
		<Card className="members-skeleton">
			<div className="members-skeleton__strip" />
			<CardHeader className="members-skeleton__header">
				<div className="members-skeleton__heading">
					<Skeleton className="members-skeleton__tile" />
					<div className="members-skeleton__lines">
						<Skeleton className="members-skeleton__line-title" />
						<Skeleton className="members-skeleton__line-sub" />
					</div>
				</div>
				<Skeleton className="members-skeleton__badge" />
			</CardHeader>
			<CardContent className="members-skeleton__content">
				{[0, 1, 2].map((i) => (
					<div key={i} className="members-skeleton__row">
						<Skeleton className="members-skeleton__avatar" />
						<div className="members-skeleton__row-lines">
							<Skeleton className="members-skeleton__row-title" />
							<Skeleton className="members-skeleton__row-sub" />
						</div>
					</div>
				))}
			</CardContent>
		</Card>
	);
}

function StateCard({
	icon: Icon,
	tone,
	title,
	description,
}: {
	icon: typeof Users;
	tone: "muted" | "destructive";
	title: string;
	description: string;
}) {
	return (
		<Card>
			<CardContent className="team-state__content">
				<div className="team-state__icon" data-tone={tone}>
					<Icon />
				</div>
				<p className="team-state__title">{title}</p>
				<p className="team-state__description">{description}</p>
			</CardContent>
		</Card>
	);
}

export function MembersByRole() {
	const { data: workers, isLoading, isError } = useWorkers();
	const { has, isAdmin } = usePermissions();
	const canListTeam = has("time_entries:list_team");
	const canManage = has("workers:manage");
	const canInvite = has("invites:create");
	const todayEntries = useTeamTimeEntries(
		dayRange(currentWorkDay()),
		canListTeam,
	);
	const setActive = useSetWorkerActive();

	const [editing, setEditing] = useState<Worker | null>(null);
	const [inviting, setInviting] = useState<Worker | null>(null);
	const [deactivating, setDeactivating] = useState<Worker | null>(null);

	async function confirmDeactivate() {
		if (!deactivating) return;
		try {
			await setActive.mutateAsync({ workerId: deactivating.id, active: false });
			toast.success("Pessoa desativada");
			setDeactivating(null);
		} catch {
			toast.error("Não foi possível desativar a pessoa.");
		}
	}

	async function reactivate(worker: Worker) {
		try {
			await setActive.mutateAsync({ workerId: worker.id, active: true });
			toast.success("Pessoa reativada");
		} catch {
			toast.error("Não foi possível reativar a pessoa.");
		}
	}

	const context: RowContext = {
		entriesByWorker: latestEntryByWorker(todayEntries.data ?? []),
		showPresence: canListTeam,
		showToggle: isAdmin,
		canManage,
		canInvite,
		onEdit: setEditing,
		onInvite: setInviting,
		onDeactivate: setDeactivating,
		onReactivate: reactivate,
	};

	if (isLoading) {
		return (
			<div className="members-by-role">
				<MembersSkeletonCard />
				<MembersSkeletonCard />
			</div>
		);
	}

	if (isError) {
		return (
			<StateCard
				icon={AlertCircle}
				tone="destructive"
				title="Não foi possível carregar a equipe"
				description="Algo deu errado ao buscar a equipe. Tente novamente em instantes."
			/>
		);
	}

	const active = activeWorkers(workers);
	const inactive = inactiveWorkers(workers);

	if (active.length === 0 && inactive.length === 0) {
		return (
			<StateCard
				icon={UserPlus}
				tone="muted"
				title="Nenhuma pessoa ainda"
				description="Cadastre ou convide alguém para começar a montar a equipe deste canteiro."
			/>
		);
	}

	const groups = groupByRole(active);

	return (
		<>
			<div className="members-by-role">
				{groups.map((group) => (
					<RoleGroupCard key={group.key} group={group} context={context} />
				))}
				{inactive.length > 0 && (
					<InactiveWorkersCard workers={inactive} context={context} />
				)}
			</div>

			<WorkerDialog
				open={editing != null}
				onOpenChange={(open) => !open && setEditing(null)}
				worker={editing ?? undefined}
			/>

			{inviting && (
				<InviteDialog
					worker={inviting}
					open
					onOpenChange={(open) => !open && setInviting(null)}
				/>
			)}

			<ConfirmDialog
				open={deactivating != null}
				onOpenChange={(open) => !open && setDeactivating(null)}
				title="Desativar pessoa"
				description={`${deactivating?.name ?? "Esta pessoa"} sai da folha de presença e deixa de bater ponto. Os registros anteriores são mantidos e você pode reativar depois.`}
				confirmLabel="Desativar"
				destructive
				isPending={setActive.isPending}
				onConfirm={confirmDeactivate}
			/>
		</>
	);
}
